import React, { useEffect, useState } from 'react'
import { NavLink } from 'react-router-dom'
import {
    Business,
    Subject,
    FormatListBulleted,
    Settings,
    ExitToApp,
    DeviceHub,
} from '@mui/icons-material'
import { useAppState } from '../../lib/app-state'
import { motion } from 'framer-motion'
import { User, Users, Users2, MapPin, LayoutDashboard } from 'lucide-react'
import { authClient, hasPermission } from '../../lib/auth-client'

const IMPERSONATION_BANNER_HEIGHT = 44

type DrawerProps = {
    title: string
    link?: string
    icon?: React.ReactNode
    permissions?: Record<string, string[]>
    isMediumScreen?: boolean
}

const DrawerItem = ({
    title,
    link = title.toLowerCase() || '/',
    icon = null,
    isMediumScreen = true,
    permissions,
}: DrawerProps) => {
    const open = useAppState((state) => state.sidebarOpen)
    const setSidebarOpen = useAppState((state) => state.setSidebarOpen)
    const { data: session, isPending } = authClient.useSession()
    const role = (session?.user as any)?.role

    if (permissions && (!role || !hasPermission(role, permissions))) {
        return null
    }

    return (
        <NavLink
            onClick={() => !isMediumScreen && setSidebarOpen(false)}
            to={link}
            className={({ isActive }) =>
                `py-2.5 px-3 mx-3 my-0.5 text-sm text-gray-600 hover:bg-accent rounded-md flex items-center font-medium transition-colors duration-150 ${isActive ? 'bg-accent text-foreground' : ''}`
            }
        >
            <div className="mr-3 flex-shrink-0 [&>svg]:!text-[20px]">
                {icon}
            </div>
            <motion.div
                initial={{ width: 0 }}
                animate={{ width: open ? 'auto' : 0 }}
                transition={{ duration: 0.3 }}
                style={{ overflow: 'hidden', whiteSpace: 'nowrap' }}
            >
                {title}
            </motion.div>
        </NavLink>
    )
}

const DrawerLine = () => {
    return <div className="border-t border-border mx-3 my-3"></div>
}

const Drawer = () => {
    const open = useAppState((state) => state.sidebarOpen)
    const toggleSidebar = useAppState((state) => state.toggleSidebar)
    const { data: session } = authClient.useSession()
    const isImpersonating = Boolean((session?.session as any)?.impersonatedBy)

    const [initialRender, setInitialRender] = useState(true)
    const [isMediumScreen, setIsMediumScreen] = useState(false)

    useEffect(() => {
        const checkScreenSize = () => {
            setIsMediumScreen(window.innerWidth >= 780)
        }

        checkScreenSize()
        window.addEventListener('resize', checkScreenSize)
        return () => window.removeEventListener('resize', checkScreenSize)
    }, [])

    useEffect(() => {
        if (initialRender) {
            setInitialRender(false)
        }
    }, [initialRender])

    const topOffset = isImpersonating ? IMPERSONATION_BANNER_HEIGHT : 0

    return (
        <>
            <motion.div
                initial={{ width: open ? 240 : isMediumScreen ? 72 : 0 }}
                animate={{ width: open ? 240 : isMediumScreen ? 72 : 0 }}
                transition={{ duration: initialRender ? 0 : 0.3 }}
                style={{ zIndex: 501, top: topOffset, height: `calc(100% - ${topOffset}px)` }}
                className="drawer bg-white border-r border-border fixed"
            >
                {(isMediumScreen || open) && (
                    <div className="py-2 flex flex-col h-full">
                        <div className="flex-1 overflow-y-auto">
                            <DrawerItem
                                isMediumScreen={isMediumScreen}
                                title="Dashboard"
                                link="dashboard"
                                icon={<LayoutDashboard size={20} />}
                                permissions={{ device: ['read'] }}
                            />
                            <DrawerItem
                                isMediumScreen={isMediumScreen}
                                title="Businesses"
                                icon={<Business />}
                                permissions={{ business: ['read'] }}
                            />
                            <DrawerItem
                                isMediumScreen={isMediumScreen}
                                title="Devices"
                                icon={<Subject />}
                            />
                            <DrawerItem
                                isMediumScreen={isMediumScreen}
                                title="Locations"
                                icon={<MapPin />}
                            />
                            <DrawerItem
                                isMediumScreen={isMediumScreen}
                                title="Models"
                                icon={<DeviceHub />}
                                permissions={{ model: ['configure'] }}
                            />
                            <DrawerLine />
                            <DrawerItem
                                isMediumScreen={isMediumScreen}
                                title="Users"
                                icon={<Users />}
                                permissions={{ user: ['list'] }}
                            />
                            <DrawerItem
                                isMediumScreen={isMediumScreen}
                                title="Logs"
                                icon={<FormatListBulleted />}
                                permissions={{ log: ['read'] }}
                            />
                            <DrawerItem
                                isMediumScreen={isMediumScreen}
                                title="Settings"
                                icon={<Settings />}
                                permissions={{ device: ['configure'] }}
                            />
                        </div>
                        <div className="pb-16">
                            <DrawerLine />
                            <DrawerItem
                                isMediumScreen={isMediumScreen}
                                title="Profile"
                                link="/profile"
                                icon={<User />}
                            />
                            <DrawerItem
                                isMediumScreen={isMediumScreen}
                                title="Sign Out"
                                link="/logout"
                                icon={<ExitToApp />}
                            />
                        </div>
                    </div>
                )}
            </motion.div>
        </>
    )
}

export { Drawer }
